import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { campaignMessages as messages } from './campaignMessages';
import type { SamplePitchProblem } from './samplePitchProblem';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SamplePitchNoticeProps {
  problem: SamplePitchProblem;
  onRetry: () => void;
  onOpenSound: () => void;
}

/** Why there is no new sample (design outreach-v2 d2): one box for each reason of the server. */
const SamplePitchNoticeComponent: React.FC<SamplePitchNoticeProps> = ({ problem, onRetry, onOpenSound }) => {
  const intl = useIntl();
  const box = (
    tone: 'warn' | 'neg',
    title: string,
    body: string,
    action: React.ReactNode = null,
    icon = faTriangleExclamation
  ) => (
    <div
      className={`${shared.alert} ${tone === 'neg' ? shared.alertNeg : shared.alertWarn}`}
      data-testid={`sample-${problem.kind}`}
    >
      <FontAwesomeIcon icon={icon} className={shared.alertIcon} />
      <div className={shared.grow}>
        <b>{title}</b>
        <div>{body}</div>
      </div>
      {action}
    </div>
  );

  switch (problem.kind) {
    case 'profileNotReady':
      return box(
        'warn',
        intl.formatMessage(messages.sampleProfileTitle),
        intl.formatMessage(messages.sampleProfileBody),
        null,
        faSparkles
      );
    case 'noSample':
      return box(
        'warn',
        intl.formatMessage(messages.sampleNoneTitle),
        intl.formatMessage(messages.sampleNoneBody),
        null,
        faSparkles
      );
    case 'songFacts':
      return box(
        'warn',
        intl.formatMessage(messages.sampleSongFactsTitle),
        intl.formatMessage(messages.sampleSongFactsBody),
        <SMLButton size="sm" outline omitCap onClick={onOpenSound}>
          {intl.formatMessage(messages.sampleOpenSound)}
        </SMLButton>
      );
    case 'rateLimited':
      return box(
        'warn',
        intl.formatMessage(messages.sampleRateTitle, { seconds: problem.seconds }),
        intl.formatMessage(messages.sampleRateBody)
      );
    case 'failed':
      return box(
        'neg',
        intl.formatMessage(messages.sampleFailedTitle),
        intl.formatMessage(messages.sampleFailedBody),
        <SMLButton size="sm" outline omitCap leftIcon={faArrowsRotate} onClick={onRetry}>
          {intl.formatMessage(messages.sampleTryAgain)}
        </SMLButton>
      );
  }
};

export const SamplePitchNotice = TypedMemo(SamplePitchNoticeComponent);
